import { useCallback, useEffect, useRef, useState } from "react"
import { useFlowBoardSession, type FlowBoardSession } from "@/components/flow/use-flow-board-session"
import { fetchShotFlowBoard, type ShotFlowBoardPayload } from "@/lib/flow-api"
import type { CastRow, LocationRow, PropRow } from "@/components/prompt-editor/mention-sources"
import type { CanvasModel, CanvasProjectStyles } from "@/types/canvas"
import type { FlowDefaults, FlowNodeRow } from "@/types/flow"
import type { ProjectSummary } from "@/types/workspace"
import type { PostProductionModelOption } from "@/lib/workspace-api"

const EMPTY_DEFAULTS: FlowDefaults = {
  image_model_id: "",
  video_model_id: "",
  frame_format: "",
}

const EMPTY_STYLES: CanvasProjectStyles = {
  visual_rendering_style: "",
  genre: "",
  tone: "",
}

const EMPTY_NODES: FlowNodeRow[] = []
const EMPTY_MODELS: CanvasModel[] = []
const EMPTY_UPSCALE: PostProductionModelOption[] = []
const EMPTY_CAST: CastRow[] = []
const EMPTY_LOCATIONS: LocationRow[] = []
const EMPTY_PROPS: PropRow[] = []

type UseShotFlowBoardArgs = {
  enabled: boolean
  project: ProjectSummary | null
  shotId: number | null
  canEdit: boolean
}

export type ShotFlowBoard = FlowBoardSession & {
  loading: boolean
  ready: boolean
}

export function useShotFlowBoard({ enabled, project, shotId, canEdit }: UseShotFlowBoardArgs): ShotFlowBoard {
  const [payload, setPayload] = useState<ShotFlowBoardPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const projectId = project?.id ?? null
  // Ответ перезагрузки применяем, только если шот не сменился, пока шёл запрос.
  const boardKeyRef = useRef<string | null>(null)
  boardKeyRef.current = enabled && projectId != null && shotId != null ? `${projectId}:${shotId}` : null

  const reloadNodes = useCallback(() => {
    if (projectId == null || shotId == null) return
    const key = `${projectId}:${shotId}`
    if (boardKeyRef.current !== key) return
    void fetchShotFlowBoard(projectId, shotId)
      .then((next) => {
        if (boardKeyRef.current === key) setPayload(next)
      })
      .catch(() => undefined)
  }, [projectId, shotId])

  useEffect(() => {
    if (!enabled || projectId == null || shotId == null) return
    let cancelled = false
    setLoading(true)
    setFailed(false)
    setPayload(null)
    void fetchShotFlowBoard(projectId, shotId)
      .then((next) => {
        if (!cancelled) setPayload(next)
      })
      .catch(() => {
        if (!cancelled) {
          setPayload(null)
          setFailed(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, projectId, shotId])

  const loaded = enabled && payload != null && !loading
  const session = useFlowBoardSession({
    project,
    board: loaded ? payload.flow : null,
    nodes: loaded ? payload.nodes : EMPTY_NODES,
    models: payload?.flow_models ?? EMPTY_MODELS,
    upscaleModels: payload?.flow_upscale_models ?? EMPTY_UPSCALE,
    defaults: payload?.flow_defaults ?? EMPTY_DEFAULTS,
    projectStyles: payload?.flow_project_styles ?? EMPTY_STYLES,
    characters: payload?.project_cast ?? EMPTY_CAST,
    locations: payload?.project_locations ?? EMPTY_LOCATIONS,
    propsList: payload?.project_props ?? EMPTY_PROPS,
    canEdit,
    reloadNodes,
    chains: [],
    onChainsChange: () => {},
    returnFlow: null,
    allowChains: false,
    selectNodeId: loaded ? payload.focus_node_id : null,
    // Шот в виде флоу: доска каждый раз открывается упорядоченной и целиком в кадре.
    openLayout: "arrange",
    insideDialog: true,
  })

  return { ...session, loading: enabled && loading, ready: !loading && (loaded || failed) }
}
